import React, { useState, useEffect } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { formatDateISO, parseDateLoose, formatDateInput } from "../../lib/formUtils";

/**
 * Editorial-styled date field. Auto-formats digits → MM/DD/YYYY as user types.
 * Also opens a popover calendar with a year dropdown for quick picking.
 *
 * Props: value (ISO date string), onChange(iso), minYear, maxYear, testId, placeholder
 */
export default function DateField({
  value,
  onChange,
  minYear,
  maxYear,
  testId,
  placeholder = "MM/DD/YYYY",
  disabled,
}) {
  const today = new Date();
  const fromYear = minYear ?? today.getFullYear() - 100;
  const toYear = maxYear ?? today.getFullYear() + 5;

  const isoToMask = (iso) => {
    const d = parseDateLoose(iso);
    if (!d) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${d.getFullYear()}`;
  };

  const [text, setText] = useState(value ? isoToMask(value) : "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setText(value ? isoToMask(value) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleTextChange = (e) => {
    const masked = formatDateInput(e.target.value);
    setText(masked);
    if (masked.length === 10) {
      const parsed = parseDateLoose(masked);
      if (parsed) onChange(formatDateISO(parsed));
    } else if (masked === "") {
      onChange("");
    }
  };

  const handleSelect = (d) => {
    if (!d) return;
    onChange(formatDateISO(d));
    setText(isoToMask(formatDateISO(d)));
    setOpen(false);
  };

  const selectedDate = value ? parseDateLoose(value) : undefined;

  return (
    <div className="relative">
      <input
        className="field-editorial pr-10"
        type="text"
        value={text}
        onChange={handleTextChange}
        placeholder={placeholder}
        autoComplete="off"
        inputMode="numeric"
        maxLength={10}
        disabled={disabled}
        data-testid={testId}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-muted hover:text-gold transition-colors"
            aria-label="Open calendar"
            disabled={disabled}
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            <CalendarIcon size={16} strokeWidth={1.25} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4 bg-ivory border border-hairline shadow-xl" align="end" sideOffset={8}>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            captionLayout="dropdown"
            fromYear={fromYear}
            toYear={toYear}
            defaultMonth={selectedDate || new Date(toYear - 5, today.getMonth())}
            className="p-0"
            classNames={{
              months: "flex flex-col space-y-3",
              month: "space-y-3",
              caption: "flex justify-center pt-1 relative items-center h-10",
              caption_label: "hidden",
              caption_dropdowns: "flex gap-2 items-center justify-center w-full",
              dropdown: "font-serif text-sm text-ink bg-ivory border border-hairline px-2 py-1 focus:outline-none focus:border-gold cursor-pointer",
              dropdown_month: "font-serif text-sm",
              dropdown_year: "font-serif text-sm",
              nav: "flex items-center gap-1",
              nav_button: "h-8 w-8 inline-flex items-center justify-center hover:bg-[#FBF7EC] text-ink transition-colors rounded-full",
              nav_button_previous: "absolute left-0",
              nav_button_next: "absolute right-0",
              head_row: "flex mt-3",
              head_cell: "text-muted uppercase tracking-[0.16em] w-10 font-light text-[10px]",
              row: "flex w-full mt-1",
              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
              day: "h-10 w-10 p-0 font-light text-ink hover:bg-[#FBF7EC] hover:text-gold rounded-full transition-colors",
              day_selected: "!bg-ink !text-ivory hover:!bg-ink hover:!text-ivory focus:!bg-ink focus:!text-ivory rounded-full",
              day_today: "border border-gold text-ink",
              day_outside: "text-muted/40",
              day_disabled: "text-muted/40 line-through",
              day_hidden: "invisible",
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
